import { c as _c } from "react/compiler-runtime";
import { Button, cn } from '@newfront-insurance/core-ui/v2';
import { useFileSelection } from '@newfront-insurance/nova-core';
import { Paperclip } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useAttachments } from './attachments-provider';
import { useDragHandlers } from './chat-input-context';
import { useChatInputContext } from './chat-input-context';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
interface ChatAttachmentsProps {
  className?: string;
}

/**
 * Component for managing file attachments in chat input.
 * Manages its own attachment state and file selection logic.
 * Contributes attachment data to additionalContext via ChatOverlayContext.
 *
 * This component is decoupled from the input setup - it simply sets additionalContext
 * which is then used when the message is sent.
 *
 * Includes only the attachment button and file input.
 * Note: The attachments preview and web search warning are rendered separately.
 */
export function ChatAttachments(t0) {
  const $ = _c(36);
  const {
    className
  } = t0;
  const {
    attachments,
    setAttachments
  } = useAttachments();
  const {
    setAdditionalContext
  } = useChatOverlayContext();
  const {
    setHandlers
  } = useDragHandlers();
  const {
    status
  } = useChatInputContext();
  let t1;
  if ($[0] !== setAttachments) {
    t1 = {
      setAttachments
    };
    $[0] = setAttachments;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    supportedFileTypes
  } = useFileSelection(t1);
  const prevStatusRef = useRef(status);
  let t2;
  let t3;
  if ($[2] !== setAdditionalContext || $[3] !== setAttachments || $[4] !== status) {
    t2 = () => {
      if (prevStatusRef.current !== "submitted" && status === "submitted") {
        setAttachments([]);
        setAdditionalContext(_temp);
      }
      prevStatusRef.current = status;
    };
    t3 = [status, setAttachments, setAdditionalContext];
    $[2] = setAdditionalContext;
    $[3] = setAttachments;
    $[4] = status;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== handleDragLeave || $[8] !== handleDragOver || $[9] !== handleDrop || $[10] !== handlePaste || $[11] !== isDragging || $[12] !== setHandlers) {
    t4 = () => {
      setHandlers({
        isDragging,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handlePaste
      });
    };
    t5 = [isDragging, handleDragOver, handleDragLeave, handleDrop, handlePaste, setHandlers];
    $[7] = handleDragLeave;
    $[8] = handleDragOver;
    $[9] = handleDrop;
    $[10] = handlePaste;
    $[11] = isDragging;
    $[12] = setHandlers;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] !== attachments || $[16] !== setAdditionalContext) {
    t6 = () => {
      const successfulAttachments = attachments.filter(_temp2);
      const attachmentFileUuids_0 = successfulAttachments.map(_temp3);
      const fileMetadata = successfulAttachments.map(_temp4);
      setAdditionalContext(prev_0 => ({
        ...prev_0,
        attachmentFileUuids: attachmentFileUuids_0,
        messageMetadata: fileMetadata.length > 0 ? {
          files: fileMetadata
        } : undefined
      }));
    };
    t7 = [attachments, setAdditionalContext];
    $[15] = attachments;
    $[16] = setAdditionalContext;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  useEffect(t6, t7);
  let t8;
  if ($[19] !== className) {
    t8 = cn("flex items-center", className);
    $[19] = className;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("text-muted-foreground h-fit rounded-full bg-transparent p-2 transition-colors", "hover:bg-rebrand-400 border-none hover:text-white active:text-white");
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Paperclip size={16} />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== handleAttachmentClick) {
    t11 = <Button data-testid="attachment-button" variant="transparent" className={t9} onClick={handleAttachmentClick} type="button">{t10}</Button>;
    $[23] = handleAttachmentClick;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== supportedFileTypes) {
    t12 = supportedFileTypes.join(",");
    $[25] = supportedFileTypes;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      display: "none"
    };
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== fileInputRef || $[29] !== handleFileUpload || $[30] !== t12) {
    t14 = <input ref={fileInputRef} type="file" accept={t12} multiple={true} onChange={handleFileUpload} style={t13} />;
    $[28] = fileInputRef;
    $[29] = handleFileUpload;
    $[30] = t12;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t11 || $[33] !== t14 || $[34] !== t8) {
    t15 = <div className={t8}>{t11}{t14}</div>;
    $[32] = t11;
    $[33] = t14;
    $[34] = t8;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
}
function _temp4(attachment_1) {
  return {
    uuid: attachment_1.fileUuid,
    filename: attachment_1.file.name
  };
}
function _temp3(attachment_0) {
  return attachment_0.fileUuid;
}
function _temp2(attachment) {
  return attachment.fileUuid && !attachment.uploadError;
}
function _temp(prev) {
  const {
    attachmentFileUuids,
    messageMetadata,
    attachments: _,
    ...rest
  } = prev;
  return rest;
}